// Minutage des bulles animées du menu Plus (Compléter un compartiment, Mélange de produits, Expansion des produits).
// Chaque bulle compare deux camions de 4 compartiments de 4 t : option désactivée en haut, activée en bas.
// Les deux chargent, la vue s'approche d'un compartiment, puis recule pour le bilan.
// sceneAt(t) donne l'état complet à l'instant t (secondes) ; l'animation n'a rien d'autre en mémoire.

export const HELP_CAPACITIES = [4, 4, 4, 4];
const PERIOD = 13.6;
// Image fixe montrée quand le mouvement est réduit : bilan affiché, vue reculée.
const STILL_TIME = 10;

export const ease = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
const clamp01 = (v) => Math.max(0, Math.min(1, v));
const fade = (t, a, b) => clamp01((t - a) / (b - a));
const step = (order, pos, f, t0, t1, label) => ({ order, pos, f, t0, t1, label });

// Remplissages (couches par compartiment), couvercle ouvert et chute de chargement au-dessus du compartiment servi.
export function truckAt(steps, t) {
  const fills = {};
  const lids = {};
  steps.forEach((s) => {
    if (t < s.t0) return;
    (fills[s.pos] = fills[s.pos] || []).push({ order: s.order, label: s.label, f: s.f * ease(clamp01((t - s.t0) / (s.t1 - s.t0))) });
  });
  const first = steps[0];
  const last = steps[steps.length - 1];
  if (t < first.t0 - 0.6 || t > last.t1 + 0.6) return { fills, lids, spout: null };
  let i = steps.findIndex((s) => t < s.t1 + 0.15);
  if (i < 0) i = steps.length - 1;
  const current = steps[i];
  lids[current.pos] = t >= current.t0 - 0.15 && t <= current.t1 + 0.1 ? 1 : 0;
  return {
    fills, lids,
    spout: {
      pos: current.pos,
      fromPos: steps[Math.max(0, i - 1)].pos,
      order: current.order,
      move: i === 0 ? 1 : ease(fade(t, current.t0 - 0.5, current.t0 - 0.1)),
      alpha: Math.min(fade(t, first.t0 - 0.6, first.t0 - 0.3), 1 - fade(t, last.t1 + 0.25, last.t1 + 0.55)),
      flow: t >= current.t0 && t <= current.t1,
    },
  };
}

// Déroulé commun : zoom entre zoomIn et zoomOut, fondu de fin de boucle.
function makeScene({ off, on, zoomIn, zoomOut, focus, marks }) {
  return {
    period: PERIOD,
    stillTime: STILL_TIME,
    focus,
    sceneAt(time) {
      const t = ((time % PERIOD) + PERIOD) % PERIOD;
      const zoomed = t > zoomIn + 1.1 && t < zoomOut;
      const after = t > zoomOut + 1 && t < 12.6;
      const extra = marks({ t, zoomed, after });
      return {
        t,
        zoom: ease(fade(t, zoomIn, zoomIn + 0.9)) * (1 - ease(fade(t, zoomOut, zoomOut + 0.9))),
        opacity: 1 - fade(t, 12.6, 13.2),
        off: { ...truckAt(off, t), ...extra.off },
        on: { ...truckAt(on, t), ...extra.on },
        show: extra.show,
      };
    },
  };
}

// Compléter un compartiment : A 6 t et B 10 t, même produit. Désactivé, les 2 t vides de C2 restent réservées à A
// et B ne tient pas dans les 8 t libres ; activé, B termine C2 puis remplit C3 et C4.
const SHARING_A = [step("A", 1, 1, 0.7, 1.5, "A"), step("A", 2, 0.5, 1.8, 2.4, "A · 2 t")];
export const sharingScene = makeScene({
  off: SHARING_A,
  on: [...SHARING_A, step("B", 2, 0.5, 4.0, 5.0, "B · 2 t"), step("B", 3, 1, 6.7, 7.4, "B"), step("B", 4, 1, 7.7, 8.4, "B")],
  zoomIn: 2.8, zoomOut: 5.6, focus: { pos: 2 },
  marks: ({ t, zoomed, after }) => ({
    off: { over: zoomed ? [2] : after ? [3, 4] : [], hot: [] },
    on: { over: [], hot: zoomed ? [2] : [] },
    show: { reserved: zoomed, refused: after && t > 6.8, off: t > 7.2 && t < 12.8, on: t > 8.6 && t < 12.8 },
  }),
});

// Mélange de produits : Trituro 8 t (C1, C2), puis T SOYA 8 t. Désactivé, l'autre produit est refusé ;
// activé, il part dans ses propres compartiments (C3, C4), jamais dans un compartiment déjà occupé.
const MIXING_A = [step("A", 1, 1, 0.7, 1.4, "Trituro"), step("A", 2, 1, 1.7, 2.4, "Trituro")];
export const mixingScene = makeScene({
  off: MIXING_A,
  on: [...MIXING_A, step("B", 3, 1, 4.0, 5.0, "T SOYA"), step("B", 4, 1, 6.7, 7.5, "T SOYA")],
  zoomIn: 2.8, zoomOut: 5.6, focus: { between: [2, 3] },
  marks: ({ t, zoomed, after }) => ({
    off: { over: zoomed || after ? [3, 4] : [], hot: [] },
    on: { over: [], hot: zoomed ? [3] : [] },
    show: { refused: zoomed, off: t > 7.2 && t < 12.8, on: t > 7.8 && t < 12.8 },
  }),
});

// Expansion des produits : 12 t d'un produit plafonné à 80 %. 80 % de 4 t = 3,2 t, arrondi vers le bas à la demi-tonne : 3 t.
// Désactivé, trois compartiments pleins à 100 % ; activé, 3 t dans chacun des quatre, 1 t de marge chacun.
export const EXPANSION_PERCENT = 80;
export const expansionLimitTons = (capacity, percent) => Math.floor((capacity * percent) / 100 / 0.5) * 0.5;
const LIMIT = expansionLimitTons(4, EXPANSION_PERCENT);
export const expansionScene = makeScene({
  off: [step("A", 1, 1, 0.7, 1.5, "4 t"), step("A", 2, 1, 1.7, 2.5, "4 t"), step("A", 3, 1, 2.7, 3.5, "4 t")],
  on: [1, 2, 3, 4].map((pos, i) => step("A", pos, LIMIT / 4, 0.7 + i * 0.75, 1.3 + i * 0.75, `${String(LIMIT).replace(".", ",")} t`)),
  zoomIn: 4.0, zoomOut: 6.8, focus: { pos: 1 },
  marks: ({ t, zoomed }) => ({
    off: { over: [], hot: zoomed ? [1] : [] },
    on: { over: [], hot: zoomed ? [1] : [], limits: Object.fromEntries([1, 2, 3, 4].map((pos) => [pos, LIMIT / 4])) },
    show: { full: zoomed, margin: zoomed, off: t > 7.9 && t < 12.8, on: t > 8.4 && t < 12.8 },
  }),
});
